import React, { createContext, useReducer, useContext, useEffect } from 'react';
import { useAuthContext } from './AuthContext';
import api from '../utils/api';

// Initial state
const initialState = {
  todos: [],
  sharedTodos: [],
  loading: true,
  error: null
};

// Create context
const TodoContext = createContext(initialState);

// Reducer function
const todoReducer = (state, action) => {
  switch (action.type) {
    case 'GET_TODOS':
      return {
        ...state,
        todos: action.payload,
        loading: false
      };
    case 'GET_SHARED_TODOS':
      return {
        ...state,
        sharedTodos: action.payload,
        loading: false
      };
    case 'ADD_TODO':
      return {
        ...state,
        todos: [action.payload, ...state.todos]
      };
    case 'UPDATE_TODO':
      return {
        ...state,
        todos: state.todos.map(todo => 
          todo._id === action.payload._id ? action.payload : todo
        ),
        sharedTodos: state.sharedTodos.map(todo =>
          todo._id === action.payload._id ? action.payload : todo
        )
      };
    case 'DELETE_TODO':
      return {
        ...state,
        todos: state.todos.filter(todo => todo._id !== action.payload),
        sharedTodos: state.sharedTodos.filter(todo => todo._id !== action.payload)
      };
    case 'TODO_ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    case 'CLEAR_TODOS':
      return {
        ...state,
        todos: [],
        sharedTodos: [],
        loading: false
      };
    default:
      return state;
  }
};

// Provider component
export const TodoProvider = ({ children }) => {
  const [state, dispatch] = useReducer(todoReducer, initialState);
  const { isAuthenticated } = useAuthContext();

  useEffect(() => {
    const fetchTodos = async () => {
      if (!isAuthenticated) {
        dispatch({ type: 'CLEAR_TODOS' });
        return;
      }

      try {
        // Fetch personal and shared todos
        const [todosRes, sharedTodosRes] = await Promise.all([
          api.get('/todos'),
          api.get('/todos/shared')
        ]);

        console.log('Fetched todos:', todosRes.data);
        console.log('Fetched shared todos:', sharedTodosRes.data);
        
        dispatch({ type: 'GET_TODOS', payload: todosRes.data });
        dispatch({ type: 'GET_SHARED_TODOS', payload: sharedTodosRes.data });
      } catch (err) {
        console.error('Error fetching todos:', err);
        dispatch({
          type: 'TODO_ERROR',
          payload: err.response?.data?.message || 'Error fetching todos'
        });
      }
    };

    fetchTodos();
  }, [isAuthenticated]);

  const addTodo = async (todo) => {
    try {
      console.log('Adding todo with data:', todo);
      const res = await api.post('/todos', todo);
      console.log('Todo added successfully:', res.data);
      
      dispatch({
        type: 'ADD_TODO',
        payload: res.data
      });
      return res.data;
    } catch (err) {
      console.error('Error adding todo:', err);
      dispatch({
        type: 'TODO_ERROR',
        payload: err.response?.data?.message || 'Error adding todo'
      });
      throw err;
    }
  };

  const updateTodo = async (id, todo) => {
    try {
      console.log('Updating todo with data:', { id, ...todo });
      const res = await api.put(`/todos/${id}`, todo);
      console.log('Todo updated successfully:', res.data);
      
      dispatch({
        type: 'UPDATE_TODO',
        payload: res.data
      });
      return res.data;
    } catch (err) {
      console.error('Error updating todo:', err);
      dispatch({
        type: 'TODO_ERROR',
        payload: err.response?.data?.message || 'Error updating todo'
      });
      throw err;
    }
  };

  const deleteTodo = async (id) => {
    try {
      await api.delete(`/todos/${id}`);
      console.log('Todo deleted successfully:', id);
      
      dispatch({
        type: 'DELETE_TODO',
        payload: id
      });
    } catch (err) {
      console.error('Error deleting todo:', err);
      dispatch({
        type: 'TODO_ERROR',
        payload: err.response?.data?.message || 'Error deleting todo'
      });
      throw err;
    }
  };

  return (
    <TodoContext.Provider
      value={{
        todos: state.todos,
        sharedTodos: state.sharedTodos,
        loading: state.loading,
        error: state.error,
        addTodo,
        updateTodo,
        deleteTodo
      }}
    >
      {children}
    </TodoContext.Provider>
  );
};

// Custom hook
export const useTodoContext = () => {
  const context = useContext(TodoContext);
  if (context === undefined) {
    throw new Error('useTodoContext must be used within a TodoProvider');
  }
  return context;
};

// Esporta anche come useTodo per compatibilità
export const useTodo = useTodoContext;

// Esporta il context se necessario
export default TodoContext;